import { z } from 'zod';
import { IsoDateSchema, IsoDateTimeSchema, UuidSchema } from './common';

/**
 * Segments + the contacts Query AST (spec §6.6 / §9, ARCHITECTURE §2 `segments`).
 *
 * The AST is data only: field references are resolved by the server against its trusted registry
 * (core columns, metrics computed from `customer_events`, `field:<definitionId>` custom fields,
 * `event:<type>:count|last_at`), never interpolated as identifiers. Anything the compiler cannot
 * evaluate exactly fails with `unsupported_query` (422) and alternatives — never an approximation.
 */

export const SEGMENT_STATUSES = ['active', 'invalid', 'archived'] as const;
export type SegmentStatus = (typeof SEGMENT_STATUSES)[number];

export const SEGMENT_EVALUATION_MODES = ['dynamic', 'static'] as const;
export type SegmentEvaluationMode = (typeof SEGMENT_EVALUATION_MODES)[number];

export const QUERY_OPERATORS = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
  'not_in',
  'contains',
  'not_contains',
  'starts_with',
  'is_set',
  'is_not_set',
  'before',
  'after',
  'within_last_days',
  'not_within_last_days',
  /** Relative window with a unit: value `{ amount, unit }` ("in the last 3 months"). */
  'within_last',
  /** "Not in the last N days/months" (contacts that never had the activity are excluded). */
  'not_within_last',
  'has_tag',
  'not_has_tag',
  'birthday_within_days',
  /** value: 1–12, `'current'` (this month in the tenant timezone) or `'next'`. */
  'birthday_in_month',
] as const;
export type QueryOperator = (typeof QUERY_OPERATORS)[number];

export const TIME_UNITS = ['days', 'weeks', 'months', 'years'] as const;
export type TimeUnit = (typeof TIME_UNITS)[number];
export const CALENDAR_UNITS = ['week', 'month', 'year'] as const;
export type CalendarUnit = (typeof CALENDAR_UNITS)[number];

/** Limits enforced by the compiler (and mirrored by the builder UI). */
export const QUERY_LIMITS = {
  maxDepth: 5,
  maxConditions: 40,
  maxListValues: 100,
  maxStringLength: 200,
  maxWindowDays: 3650,
  /** Largest audience a segment can resolve to (campaigns, snapshots, AI drafts). */
  maxAudience: 50_000,
  maxSample: 25,
  /** Event-property filters (`where`) on one event aggregate condition. */
  maxPropertyFilters: 10,
} as const;

export const RelativeAmountSchema = z.strictObject({
  amount: z.number().int().min(1).max(3650),
  unit: z.enum(TIME_UNITS),
});
export type RelativeAmount = z.infer<typeof RelativeAmountSchema>;

/**
 * Time window for aggregate predicates (metrics/event counts), evaluated in the tenant timezone
 * with literal day boundaries:
 *  - `last`: from local midnight N units ago until now ("τους τελευταίους 6 μήνες")
 *  - `between`: inclusive local dates
 *  - `before` (strictly before the date) / `after` (strictly after the date)
 *  - `this` / `previous`: calendar week (Mon–Sun), month or year
 */
export const QueryWindowSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('last'),
    amount: z.number().int().min(1).max(3650),
    unit: z.enum(TIME_UNITS),
  }),
  z.strictObject({ kind: z.literal('between'), from: IsoDateSchema, to: IsoDateSchema }),
  z.strictObject({ kind: z.literal('before'), date: IsoDateSchema }),
  z.strictObject({ kind: z.literal('after'), date: IsoDateSchema }),
  z.strictObject({ kind: z.literal('this'), unit: z.enum(CALENDAR_UNITS) }),
  z.strictObject({ kind: z.literal('previous'), unit: z.enum(CALENDAR_UNITS) }),
]);
export type QueryWindow = z.infer<typeof QueryWindowSchema>;

const Scalar = z.union([z.string().max(QUERY_LIMITS.maxStringLength), z.number(), z.boolean()]);
export const QueryValueSchema = z.union([
  Scalar,
  z.array(z.union([z.string().max(QUERY_LIMITS.maxStringLength), z.number()])).max(100),
  RelativeAmountSchema,
  z.null(),
]);

/**
 * A filter on the properties of the events an event aggregate counts (`event:<type>:count` /
 * `event:<type>:last_at`), e.g. `{ property: 'product_category', op: 'eq', value: 'Τρυπάνια' }`.
 * `property` is the event field's key (or `field:<definitionId>`, normalized to the key); it is
 * resolved against the tenant's `event:<type>` field definitions and compared as a bound value —
 * never used as an identifier. Operators follow the property's kind (`OPS_BY_KIND`).
 */
export const EventPropertyFilterSchema = z.strictObject({
  property: z.string().min(1).max(200),
  op: z.enum(QUERY_OPERATORS),
  value: QueryValueSchema.optional(),
});
export type EventPropertyFilter = z.infer<typeof EventPropertyFilterSchema>;

export const QueryConditionSchema = z.strictObject({
  /**
   * Core field (`first_name`, `tags`, `consent_sms`, `birth_date`, `created_at` …), metric
   * (`metric:purchase_count`, `metric:total_spend_cents`, `metric:last_visit_at` …), custom field
   * (`field:<definitionId>`; a field key is accepted and normalized to the id) or event aggregate
   * (`event:<type>:count`, `event:<type>:last_at`).
   */
  field: z.string().min(1).max(200),
  op: z.enum(QUERY_OPERATORS),
  value: QueryValueSchema.optional(),
  /** Only for aggregates (counts/spend); omitted = lifetime. */
  window: QueryWindowSchema.optional(),
  /**
   * Only for event aggregates (`event:<type>:count|last_at`): count/consider only the events
   * whose properties match every filter (AND), e.g. purchases of category «Τρυπάνια».
   */
  where: z.array(EventPropertyFilterSchema).min(1).max(QUERY_LIMITS.maxPropertyFilters).optional(),
});
export type QueryCondition = z.infer<typeof QueryConditionSchema>;

export type QueryNode =
  { and: QueryNode[] } | { or: QueryNode[] } | { not: QueryNode } | QueryCondition;

const QueryNodeInner: z.ZodType<QueryNode> = z.lazy(() =>
  z.union([
    // `and: []` = every active customer.
    z.strictObject({ and: z.array(QueryNodeInner).max(50) }),
    z.strictObject({ or: z.array(QueryNodeInner).min(1).max(50) }),
    z.strictObject({ not: QueryNodeInner }),
    QueryConditionSchema,
  ]),
);

/**
 * Raw JSON nesting allowed before the recursive schema runs (objects and arrays each count one
 * level, so this is far above `QUERY_LIMITS.maxDepth`, which the compiler enforces). Checked
 * with bounded recursion so a hostile, deeply nested payload is a 422, not a stack overflow.
 */
export const MAX_RAW_QUERY_NESTING = 32;
function withinNesting(v: unknown, depth = 0): boolean {
  if (depth > MAX_RAW_QUERY_NESTING) return false;
  if (Array.isArray(v)) return v.every((k) => withinNesting(k, depth + 1));
  if (v !== null && typeof v === 'object')
    return Object.values(v).every((k) => withinNesting(k, depth + 1));
  return true;
}

export const QueryNodeSchema: z.ZodType<QueryNode> = z
  .unknown()
  .refine((v) => withinNesting(v), { message: 'The query is nested too deeply (too_complex)' })
  .pipe(QueryNodeInner);

/** Spec §6.6 query AST (validated server-side against the tenant registry before execution). */
export const QueryAstSchema = z.object({
  version: z.literal(1).default(1),
  entity: z.literal('contacts'),
  where: QueryNodeSchema,
  limit: z.number().int().min(1).max(50000).optional(),
  purpose: z.enum(['marketing_segmentation', 'analysis', 'review_request']).default('analysis'),
});
export type QueryAst = z.infer<typeof QueryAstSchema>;

export function isGroup(n: QueryNode): n is { and: QueryNode[] } | { or: QueryNode[] } {
  return 'and' in n || 'or' in n;
}

/** Depth and condition count of a node (for client-side limits; the server re-checks). */
export function measureQuery(n: QueryNode, depth = 0): { depth: number; conditions: number } {
  if ('and' in n || 'or' in n) {
    const kids = 'and' in n ? n.and : n.or;
    let d = depth;
    let c = 0;
    for (const k of kids) {
      const m = measureQuery(k, depth + 1);
      d = Math.max(d, m.depth);
      c += m.conditions;
    }
    return { depth: d, conditions: c };
  }
  if ('not' in n) return measureQuery(n.not, depth + 1);
  return { depth, conditions: 1 };
}

// ─── Field catalog (what the builder may offer; computed by the server) ─────────────────────
export const QUERY_FIELD_KINDS = [
  'text',
  'number',
  'money',
  'date',
  'enum',
  'multi_enum',
  'boolean',
  'tags',
  'consent',
  'birthday',
  'presence',
  /** Aggregate count with an optional time window (purchases, visits, events). */
  'count',
  /** Aggregate money (cents) with an optional time window (spend). */
  'spend',
] as const;
export type QueryFieldKind = (typeof QUERY_FIELD_KINDS)[number];

export const QUERY_FIELD_GROUPS = ['profile', 'consent', 'activity', 'events', 'custom'] as const;
export type QueryFieldGroup = (typeof QUERY_FIELD_GROUPS)[number];

/** Operators allowed per field kind (server is authoritative; the UI uses this to render). */
export const OPS_BY_KIND: Record<QueryFieldKind, readonly QueryOperator[]> = {
  text: ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'is_set', 'is_not_set'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_set', 'is_not_set'],
  money: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_set', 'is_not_set'],
  date: [
    'within_last',
    'not_within_last',
    'within_last_days',
    'not_within_last_days',
    'before',
    'after',
    'between',
    'is_set',
    'is_not_set',
  ],
  enum: ['eq', 'neq', 'in', 'not_in', 'is_set', 'is_not_set'],
  multi_enum: ['contains', 'not_contains', 'in', 'is_set', 'is_not_set'],
  boolean: ['eq', 'is_set', 'is_not_set'],
  tags: ['has_tag', 'not_has_tag', 'in', 'is_set', 'is_not_set'],
  consent: ['eq', 'neq', 'in', 'not_in'],
  birthday: ['birthday_within_days', 'birthday_in_month', 'is_set', 'is_not_set'],
  presence: ['is_set', 'is_not_set'],
  count: ['gte', 'gt', 'lte', 'lt', 'eq', 'between'],
  spend: ['gte', 'gt', 'lte', 'lt', 'between'],
};

export const BilingualTextSchema = z.object({ el: z.string(), en: z.string() });
export type BilingualText = z.infer<typeof BilingualTextSchema>;

/** A filterable event property offered with an event aggregate field (catalog). */
export const QueryPropertySchema = z.object({
  /** Property key as stored in `customer_events.properties` (used in `where[].property`). */
  key: z.string(),
  label: BilingualTextSchema,
  kind: z.enum(QUERY_FIELD_KINDS),
  ops: z.array(z.enum(QUERY_OPERATORS)),
  values: z.array(z.object({ value: z.string(), label: BilingualTextSchema })).optional(),
  /** Money properties are stored in plain euros (not cents). */
  unit: z.enum(['eur']).optional(),
});
export type QueryProperty = z.infer<typeof QueryPropertySchema>;

export const QueryFieldSchema = z.object({
  /** AST reference (`first_name`, `metric:total_spend_cents`, `field:<id>`, `event:<type>:count`). */
  ref: z.string(),
  label: BilingualTextSchema,
  description: BilingualTextSchema.nullable(),
  group: z.enum(QUERY_FIELD_GROUPS),
  kind: z.enum(QUERY_FIELD_KINDS),
  ops: z.array(z.enum(QUERY_OPERATORS)),
  /** Allowed values (enum fields, consent states, tags in use). */
  values: z.array(z.object({ value: z.string(), label: BilingualTextSchema })).optional(),
  /** Aggregates accept a time window. */
  windowed: z.boolean(),
  /** Where the value comes from (shown as provenance). */
  source: z.enum(['contacts', 'custom_field', 'customer_events']),
  /** Event aggregates: properties usable in the condition's `where` filters. */
  properties: z.array(QueryPropertySchema).optional(),
});
export type QueryField = z.infer<typeof QueryFieldSchema>;

export const QueryCatalogSchema = z.object({
  fields: z.array(QueryFieldSchema),
  timezone: z.string(),
  today: IsoDateSchema,
  schemaVersion: z.number().int(),
  limits: z.object({
    maxDepth: z.number().int(),
    maxConditions: z.number().int(),
    maxAudience: z.number().int(),
  }),
});
export type QueryCatalog = z.infer<typeof QueryCatalogSchema>;

// ─── Evaluation results ─────────────────────────────────────────────────────────────────────
export const ExplainConditionSchema = z.object({
  /** Path in the AST, e.g. `0.1` (group child indices). */
  path: z.string(),
  field: z.string(),
  text: BilingualTextSchema,
  source: z.enum(['contacts', 'custom_field', 'customer_events']),
  /** Literal local-date bounds the condition used (tenant timezone), when time-based. */
  range: z.object({ from: IsoDateSchema.nullable(), to: IsoDateSchema.nullable() }).nullable(),
});
export type ExplainCondition = z.infer<typeof ExplainConditionSchema>;

export const QueryExplanationSchema = z.object({
  summary: BilingualTextSchema,
  conditions: z.array(ExplainConditionSchema),
});
export type QueryExplanation = z.infer<typeof QueryExplanationSchema>;

export const SEGMENT_EXCLUSION_REASONS = [
  'no_consent',
  'suppressed',
  'missing_address',
  'invalid_address',
] as const;

export const ChannelEligibilitySchema = z.object({
  eligible: z.number().int(),
  excluded: z.object({
    no_consent: z.number().int(),
    suppressed: z.number().int(),
    missing_address: z.number().int(),
    invalid_address: z.number().int(),
  }),
});
export type ChannelEligibility = z.infer<typeof ChannelEligibilitySchema>;

/** Marketing eligibility (same rules as messaging: consent, existing-customer basis, suppression, address). */
export const EligibilityBreakdownSchema = z.object({
  policy: z.literal('marketing'),
  total: z.number().int(),
  sms: ChannelEligibilitySchema,
  email: ChannelEligibilitySchema,
  /** Reachable on at least one channel. */
  anyChannel: z.number().int(),
  /** Contacts whose marketing permission rests on the existing-customer basis (consent unknown). */
  viaExistingCustomer: z.object({ sms: z.number().int(), email: z.number().int() }),
});
export type EligibilityBreakdown = z.infer<typeof EligibilityBreakdownSchema>;

export const QUERY_WARNING_CODES = [
  'no_events_recorded',
  'unmatched_events',
  'no_visit_events',
  'no_purchase_events',
  'birthday_unknown',
  'enum_value_unknown',
  'never_active_excluded',
  'eligibility_skipped',
  'snapshot',
] as const;
export type QueryWarningCode = (typeof QUERY_WARNING_CODES)[number];

export const QueryWarningSchema = z.object({
  code: z.enum(QUERY_WARNING_CODES),
  params: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
});
export type QueryWarning = z.infer<typeof QueryWarningSchema>;

export const SegmentSampleRowSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  tags: z.array(z.string()),
  hasEmail: z.boolean(),
  hasPhone: z.boolean(),
  consentSms: z.enum(['granted', 'denied', 'unknown']),
  consentEmail: z.enum(['granted', 'denied', 'unknown']),
  visitCount: z.number().int(),
  purchaseCount: z.number().int(),
  totalSpendCents: z.number().int(),
  lastActivityAt: IsoDateTimeSchema.nullable(),
});
export type SegmentSampleRow = z.infer<typeof SegmentSampleRowSchema>;

export const EvaluationMetaSchema = z.object({
  evaluatedAt: IsoDateTimeSchema,
  timezone: z.string(),
  today: IsoDateSchema,
  /** `live`: computed now from contacts + customer_events; `snapshot`: frozen member list. */
  mode: z.enum(['live', 'snapshot']),
  schemaVersion: z.number().int(),
  latestEventAt: IsoDateTimeSchema.nullable(),
  latestIngestedAt: IsoDateTimeSchema.nullable(),
  snapshotAt: IsoDateTimeSchema.nullable(),
  durationMs: z.number().int(),
  warnings: z.array(QueryWarningSchema),
});
export type EvaluationMeta = z.infer<typeof EvaluationMetaSchema>;

export const SegmentEvaluationSchema = z.object({
  count: z.number().int(),
  sample: z.array(SegmentSampleRowSchema),
  eligibility: EligibilityBreakdownSchema.nullable(),
  explanation: QueryExplanationSchema,
  meta: EvaluationMetaSchema,
  /** The normalized AST that was evaluated (custom fields referenced by id). */
  ast: QueryAstSchema,
});
export type SegmentEvaluation = z.infer<typeof SegmentEvaluationSchema>;

export const SegmentPreviewRequestSchema = z.object({
  ast: QueryAstSchema,
  sampleSize: z.number().int().min(0).max(QUERY_LIMITS.maxSample).default(8),
});
export type SegmentPreviewRequest = z.input<typeof SegmentPreviewRequestSchema>;

/** Shape of `details` on a 422 `unsupported_query`. */
export const UnsupportedQueryDetailsSchema = z.object({
  reason: z.enum([
    'unknown_field',
    'field_not_filterable',
    'operator_not_supported',
    'invalid_value',
    'window_not_supported',
    'too_complex',
    'too_large',
    'timeout',
    'semantic_text',
    'not_understood',
  ]),
  path: z.string().optional(),
  field: z.string().optional(),
  op: z.string().optional(),
  supportedOps: z.array(z.string()).optional(),
  alternatives: z.array(BilingualTextSchema).default([]),
  /** Natural-language requests: the part of the text that could not be mapped to a rule. */
  unparsed: z.string().optional(),
  /** Natural-language requests: the AI run that produced the interpretation (credits were used). */
  runId: UuidSchema.optional(),
  creditsCharged: z.number().int().optional(),
});
export type UnsupportedQueryDetails = z.infer<typeof UnsupportedQueryDetailsSchema>;

// ─── Natural language → AST ─────────────────────────────────────────────────────────────────
export const InterpretRequestSchema = z.object({
  text: z.string().trim().min(3).max(500),
  locale: z.enum(['el', 'en']).optional(),
});
export type InterpretRequest = z.infer<typeof InterpretRequestSchema>;

export const InterpretResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    ast: QueryAstSchema,
    /** Short restatement of what was understood (model output, shown as such). */
    interpretation: z.string(),
    suggestedName: z.string(),
    preview: SegmentEvaluationSchema,
    runId: UuidSchema,
    creditsCharged: z.number().int(),
  }),
  z.object({
    status: z.literal('clarify'),
    question: z.string(),
    /** Full rephrasings the user can pick (sent back as `text`). */
    options: z.array(z.string()),
    runId: UuidSchema,
    creditsCharged: z.number().int(),
  }),
]);
export type InterpretResponse = z.infer<typeof InterpretResponseSchema>;

// ─── Segments CRUD ──────────────────────────────────────────────────────────────────────────
export const InvalidRefSchema = z.object({
  ref: z.string(),
  label: z.string().nullable(),
  reason: z.enum(['deprecated', 'deleted', 'not_filterable', 'type_changed', 'unsupported']),
});
export type InvalidRef = z.infer<typeof InvalidRefSchema>;

export const SegmentSnapshotInfoSchema = z.object({
  id: UuidSchema,
  memberCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});

export const SegmentSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  queryAst: QueryAstSchema,
  naturalLanguage: z.string().nullable(),
  explanation: QueryExplanationSchema.nullable(),
  schemaVersion: z.number().int(),
  evaluationMode: z.enum(SEGMENT_EVALUATION_MODES),
  status: z.enum(SEGMENT_STATUSES),
  invalidReason: z.string().nullable(),
  invalidRefs: z.array(InvalidRefSchema),
  lastEvaluatedAt: IsoDateTimeSchema.nullable(),
  lastKnownCount: z.number().int().nullable(),
  lastEligibility: EligibilityBreakdownSchema.nullable(),
  snapshot: SegmentSnapshotInfoSchema.nullable(),
  createdBy: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Segment = z.infer<typeof SegmentSchema>;

export const SegmentListResponseSchema = z.object({ items: z.array(SegmentSchema) });

export const SegmentDetailResponseSchema = z.object({
  segment: SegmentSchema,
  /** Fresh evaluation (live for dynamic, snapshot members for static); null when invalid. */
  preview: SegmentEvaluationSchema.nullable(),
});
export type SegmentDetailResponse = z.infer<typeof SegmentDetailResponseSchema>;

export const CreateSegmentRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(500).nullish(),
  queryAst: QueryAstSchema,
  naturalLanguage: z.string().trim().max(500).nullish(),
  evaluationMode: z.enum(SEGMENT_EVALUATION_MODES).default('dynamic'),
  /** Promote an assistant draft (`ai_drafts`, kind segment) into this segment. */
  aiDraftId: UuidSchema.optional(),
});
export type CreateSegmentRequest = z.input<typeof CreateSegmentRequestSchema>;

export const UpdateSegmentRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(500).nullable(),
    queryAst: QueryAstSchema,
    naturalLanguage: z.string().trim().max(500).nullable(),
    status: z.enum(['active', 'archived']),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateSegmentRequest = z.input<typeof UpdateSegmentRequestSchema>;

export const FreezeSegmentRequestSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
});
export type FreezeSegmentRequest = z.input<typeof FreezeSegmentRequestSchema>;

export const SegmentListQuerySchema = z.object({
  status: z.enum(['active', 'invalid', 'archived', 'all']).optional(),
});

/** Replace references that became invalid (deprecated/deleted fields) with valid ones. */
export const RemapSegmentRequestSchema = z.object({
  mapping: z
    .array(z.object({ from: z.string().min(1).max(200), to: z.string().min(1).max(200) }))
    .min(1)
    .max(QUERY_LIMITS.maxConditions),
});
export type RemapSegmentRequest = z.input<typeof RemapSegmentRequestSchema>;

export const RefreshSegmentsResponseSchema = z.object({
  refreshed: z.number().int(),
  failed: z.number().int(),
  items: z.array(SegmentSchema),
});
export type RefreshSegmentsResponse = z.infer<typeof RefreshSegmentsResponseSchema>;

/** An assistant segment draft (`ai_drafts`) as the builder loads it. */
export const SegmentAiDraftSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  status: z.enum(['draft', 'opened', 'promoted', 'discarded']),
  name: z.string(),
  description: z.string().nullable(),
  queryAst: QueryAstSchema,
  /** Segment created from this draft (when promoted). */
  segmentId: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type SegmentAiDraft = z.infer<typeof SegmentAiDraftSchema>;

// ─── Event breakdown (assistant analytics over event properties) ────────────────────────────
export const EVENT_BREAKDOWN_METRICS = ['count', 'value', 'contacts', 'sum'] as const;
export type EventBreakdownMetric = (typeof EVENT_BREAKDOWN_METRICS)[number];

/**
 * Aggregate one event type's history, optionally grouped by one of its properties ("top products
 * this month", "revenue per category"). Same property filters and windows as segment conditions.
 *  - `count`: number of events · `value`: sum of `value_cents` · `contacts`: distinct customers
 *  - `sum`: sum of the numeric/money property `sumProperty`
 */
export const EventBreakdownRequestSchema = z.strictObject({
  eventKey: z.string().min(1).max(64),
  groupBy: z.string().min(1).max(200).optional(),
  metric: z.enum(EVENT_BREAKDOWN_METRICS).default('count'),
  sumProperty: z.string().min(1).max(200).optional(),
  where: z.array(EventPropertyFilterSchema).max(QUERY_LIMITS.maxPropertyFilters).optional(),
  window: QueryWindowSchema.optional(),
  /** Anonymous sales (no customer on purpose) are counted unless excluded. */
  includeAnonymous: z.boolean().default(true),
  limit: z.number().int().min(1).max(50).default(10),
});
export type EventBreakdownRequest = z.input<typeof EventBreakdownRequestSchema>;

export const EventBreakdownRowSchema = z.object({
  /** Group value as stored (null = property missing on those events). */
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  count: z.number().int(),
  valueCents: z.number().int(),
  contacts: z.number().int(),
  sum: z.number().nullable(),
});
export type EventBreakdownRow = z.infer<typeof EventBreakdownRowSchema>;

export const EventBreakdownResultSchema = z.object({
  eventKey: z.string(),
  eventLabel: BilingualTextSchema,
  groupBy: z.string().nullable(),
  groupLabel: BilingualTextSchema.nullable(),
  metric: z.enum(EVENT_BREAKDOWN_METRICS),
  sumProperty: z.string().nullable(),
  rows: z.array(EventBreakdownRowSchema),
  /** Groups beyond `limit` (not listed). */
  otherGroups: z.number().int(),
  totals: z.object({
    count: z.number().int(),
    valueCents: z.number().int(),
    contacts: z.number().int(),
    anonymous: z.number().int(),
    sum: z.number().nullable(),
  }),
  /** Literal local dates of the window (inclusive), null = unbounded. */
  range: z.object({ from: IsoDateSchema.nullable(), to: IsoDateSchema.nullable() }),
  /** Human-readable filters (explanation texts). */
  filters: z.array(BilingualTextSchema),
  timezone: z.string(),
});
export type EventBreakdownResult = z.infer<typeof EventBreakdownResultSchema>;
